export interface EvidenceSource {
  title: string;
  url: string;
  published_at?: string;
  accessed_at?: string;
}
export interface CapacityClaim {
  label?: string;
  mw?: number;
  mw_min?: number;
  mw_max?: number;
  basis?: string;
  scope?: string;
  status?: string;
  target_delivery?: string;
  source_url?: string;
}
export interface ReviewEvidence {
  reviewed_at?: string;
  status_as_of?: string;
  operating_status?: string;
  location_precision?: string;
  comparison_eligible?: boolean;
  canonical_campus_id?: string;
  capacity_basis?: string;
  target_delivery?: string;
  capacity_claims?: CapacityClaim[];
  sources?: EvidenceSource[];
  notes?: string[];
  stated_deeded_acres?: number;
  availability?: string;
  acreage_qualification?: string;
  caveats?: string[];
}

export function safeSourceUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return;
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' || url.protocol === 'http:') return url.href;
  } catch { /* Invalid source links remain unavailable. */ }
}

export function parseReview(value: unknown): ReviewEvidence {
  try {
    const raw = typeof value === 'string' ? JSON.parse(value) : value;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const result: Record<string, unknown> = {};
    for (const key of ['reviewed_at', 'status_as_of', 'operating_status', 'location_precision', 'canonical_campus_id', 'capacity_basis', 'target_delivery', 'availability', 'acreage_qualification']) {
      if (typeof raw[key] === 'string') result[key] = raw[key];
    }
    if (typeof raw.comparison_eligible === 'boolean') result.comparison_eligible = raw.comparison_eligible;
    if (typeof raw.stated_deeded_acres === 'number' && Number.isFinite(raw.stated_deeded_acres) && raw.stated_deeded_acres >= 0) result.stated_deeded_acres = raw.stated_deeded_acres;
    for (const key of ['notes', 'caveats']) {
      result[key] = Array.isArray(raw[key]) ? raw[key].filter((item: unknown) => typeof item === 'string') : [];
    }
    result.sources = Array.isArray(raw.sources) ? raw.sources.filter((s: EvidenceSource) => s && typeof s.title === 'string' && safeSourceUrl(s.url)).map((s: EvidenceSource) => ({
      title: s.title, url: safeSourceUrl(s.url)!, published_at: typeof s.published_at === 'string' ? s.published_at : undefined, accessed_at: typeof s.accessed_at === 'string' ? s.accessed_at : undefined,
    })) : [];
    result.capacity_claims = Array.isArray(raw.capacity_claims) ? raw.capacity_claims.filter((c: unknown) => c && typeof c === 'object').map((c: Record<string, unknown>) => {
      const claim: Record<string, unknown> = {};
      for (const key of ['label', 'basis', 'scope', 'status', 'target_delivery']) if (typeof c[key] === 'string') claim[key] = c[key];
      for (const key of ['mw', 'mw_min', 'mw_max']) if (typeof c[key] === 'number' && Number.isFinite(c[key]) && c[key] >= 0) claim[key] = c[key];
      claim.source_url = safeSourceUrl(c.source_url);
      return claim;
    }) : [];
    return result as ReviewEvidence;
  } catch { return {}; }
}

export function preciseLocation(review: ReviewEvidence): boolean {
  return review.location_precision === 'site' || review.location_precision === 'address';
}
export function capacityLabel(review: ReviewEvidence): string {
  return review.capacity_basis || 'Reported capacity · basis unverified';
}
export function capacityClaimValue(claim: CapacityClaim): string {
  if (claim.mw != null) return `${claim.mw.toLocaleString()} MW`;
  if (claim.mw_min != null && claim.mw_max != null) return `${claim.mw_min.toLocaleString()}–${claim.mw_max.toLocaleString()} MW`;
  if (claim.mw_min != null) return `At least ${claim.mw_min.toLocaleString()} MW`;
  if (claim.mw_max != null) return `Up to ${claim.mw_max.toLocaleString()} MW`;
  return 'Not disclosed';
}
export function landQualification(review: ReviewEvidence): string {
  if (review.acreage_qualification === 'source_unit_error') return 'Source acreage unit error — requires correction';
  if (review.stated_deeded_acres == null) return 'Deeded acreage not verified';
  return review.stated_deeded_acres >= 2500 ? 'At least 2,500 stated deeded acres' : 'Below 2,500 stated deeded acres';
}
